import { getApiEndpoint } from '../config/api.js';

export interface NewsArticle {
  eventId: string;
  ts: string;
  url: string;
  title: string;
  excerpt?: string;
  source?: string;
  domain?: string;
  createdAt: string;
}

export interface ArticleExtraction {
  keys: string[];
  evidence?: string[];
  summary?: string;
  confidence?: number;
  extractedAt?: string;
}

export interface NewsArticleDetail extends NewsArticle {
  targetedInstruments: string[];
  storylineInstruments: string[];
  articleHash?: string;
  extraction?: ArticleExtraction;
}

interface NewsQueueResponse {
  success: boolean;
  items: NewsArticle[];
  cursor: string | null;
}

interface NewsDetailResponse {
  success: boolean;
  detail: NewsArticleDetail;
}

interface TriggerResponse {
  success: boolean;
  message?: string;
  instrumentId?: string;
  eventId?: string;
  error?: string;
}

const handleErrorResponse = async (res: Response) => {
  let message = `Request failed with status ${res.status}`;
  try {
    const body = await res.clone().json();
    if (body?.error) message = body.error;
  } catch {
    // ignore JSON parse errors
  }
  throw new Error(message);
};

export async function fetchNewsQueue(params?: {
  limit?: number;
  cursor?: string | null;
}): Promise<NewsQueueResponse> {
  const limit = params?.limit ?? 25;
  const base = new URL(`${getApiEndpoint()}/intelligence/steward/news`);
  base.searchParams.set('limit', String(Math.max(1, Math.min(limit, 50))));
  if (params?.cursor) {
    base.searchParams.set('cursor', params.cursor);
  }

  const res = await fetch(base.toString());
  if (!res.ok) {
    await handleErrorResponse(res);
  }
  return res.json() as Promise<NewsQueueResponse>;
}

export async function fetchNewsDetail(eventId: string): Promise<NewsArticleDetail> {
  const url = `${getApiEndpoint()}/intelligence/steward/news/${encodeURIComponent(eventId)}`;
  const res = await fetch(url);
  if (!res.ok) {
    await handleErrorResponse(res);
  }
  const json = (await res.json()) as NewsDetailResponse;
  return json.detail;
}

export async function triggerStoryline(
  eventId: string,
  instrumentId: string
): Promise<void> {
  const url = `${getApiEndpoint()}/intelligence/steward/news/${encodeURIComponent(
    eventId
  )}/trigger`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ instrumentId }),
  });
  if (!res.ok) {
    await handleErrorResponse(res);
  }
  const json = (await res.json()) as TriggerResponse;
  if (!json.success) {
    throw new Error(json.error || 'Failed to trigger storyline');
  }
}

